
import { defineComponent, ref, onMounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import type { ElForm } from "element-plus"
import { CardLanguageOption } from "@/store/modules/CardLanguageModule"

type FormInstance = InstanceType<typeof ElForm>;

interface CardVersionForm {
  name: string;
  sort: number;
  card_language_id: number | null;
}

export default defineComponent({
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建版本", ["抽卡系统", "版本管理;card_versions"])
      } else {
        setCurrentPageBreadcrumbs("编辑版本", ["抽卡系统", "版本管理;card_versions"])
      }
      // 加载语系选项
      store.dispatch(Actions.GET_ALL_CARD_LANGUAGES).then(() => {
        languageOptions.value = store.getters.currentAllCardLanguages
      })
    })

    const languageOptions = ref<CardLanguageOption[]>([])

    const cardVersion = ref<CardVersionForm>({
      name: '',
      sort: 0,
      card_language_id: null
    })

    const rules = ref({
      name: [
        { required: true, message: '请输入版本名称', trigger: 'blur' },
        { min: 1, max: 20, message: '名称长度需在 1-20 之间', trigger: 'blur' }
      ]
    })

    // 加载详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_CARD_VERSION, route.params.id)
        .then(() => {
          const data = store.getters.currentCardVersion
          cardVersion.value = {
            name: data.name,
            sort: data.sort || 0,
            card_language_id: data.card_language_id || null
          }
        })
        .catch(() => {
          ElMessage.error('获取版本详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = { ...cardVersion.value }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_CARD_VERSION, params)
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('创建成功')
                  router.push({ name: 'card_versions' })
                } else {
                  ElMessage.error(data.error_msg || '创建失败')
                }
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_CARD_VERSION, {
                id: route.params.id,
                values: params
              })
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('更新成功')
                  router.push({ name: 'card_versions' })
                } else {
                  ElMessage.error(data.error_msg || '更新失败')
                }
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'card_versions' })
    }

    return {
      formRef,
      cardVersion,
      rules,
      isNew,
      languageOptions,
      onSubmit,
      onCancel
    }
  }
})
